<template>
  <BaseBody>
    <BaseTopBar>
      <BaseTopBarContent className="left">
        {{ $t('sidebar.warehousemanagement') }} > {{ warehouse.warehouse_name }}
      </BaseTopBarContent>
      <BaseTopBarContent className="right">
        <div
          v-if="!loading"
          class="action">
          <BaseActionDropdown
            v-if="hasRoleDelete"
            :icon="`fas fa-cog`"
            :text="$t('button.options')"
            :items="[
              {
                icon: 'fas fa-trash',
                iconClassName: 'text-danger',
                text: $t('button.delete'),
                textClassName: 'text-danger',
                value: 'delete',
              }
            ]"
            @onSelect="handleSelectOption"
          />
        </div>
      </BaseTopBarContent>
    </BaseTopBar>
    <BaseContent :loading="loading">
      <WmsWarehouseForm
        :state="warehouse"
        :warehouses="warehouses"
        :products="products"
        :binLocations="warehouse.bin_locations"
        :isSubmitted="isSubmitted"
        :disabled="disabled || !hasRoleEdit"
        :form.sync="form"
        :showActionButtons="hasRoleEdit"
        @onSubmit="handleSubmit"
      />
    </BaseContent>

    <baseConfirmDeleteModal
      :show="modal.show"
      :loading="modal.loading"
      @onClose="handleCloseModal"
      @onCancel="handleCancleModal"
      @onSubmit="handleSumitModal"
    />
    <BaseAlertModal
      :id="deletePrimaryWarehoseWarningModal"
      :headerText="$t('message.primarywarehousecannotbedeleted')"
    />
    <BaseConfirmDiscardModal
      :show="confirmDiscardModal.show"
      @onCancel="handleDiscardCancleModal"
      @onConfirm="handleDiscardConfirmModal"
    />
  </BaseBody>
</template>

<script>
import { mapGetters } from 'vuex'
import sidebarModule from './../../sidebarModule'
import submenuModule from '../submenuModule'
import discardFormMixin from '@/mixins/discardForm'
import userPermissionMixin from '@/mixins/userPermission'

export default {
  mixins: [discardFormMixin, userPermissionMixin],

  data () {
    return {
      loading: true,
      isSubmitted: false,
      disabled: false,
      query: {
        id: this.$route.query.id,
        name: this.$route.query.name,
      },
      modal: {
        show: false,
        loading: false,
      },
      deletePrimaryWarehoseWarningModal: 'deletePrimaryWarehoseWarningModal',
      form: null,
    }
  },

  computed: {
    ...mapGetters({
      sidebar: 'getSidebar',
      warehouses: 'getWarehouses',
      warehouse: 'getWarehouse',
      products: 'getProducts',
    }),
  },

  watch: {
    warehouses: function (newVal, oldVal) {
      sidebarModule.init(this.getObject(require('../sidebar.json')))
      sidebarModule.update(this.sidebar, 1, submenuModule.getWarehouses(this.warehouses))
      sidebarModule.update(this.sidebar, 8, [{
        'name': this.query.name,
        'router': {
          'name': 'WarehouseEdit',
          'query': {
            id: this.query.id,
            name: this.query.name,
          },
        },
        'activeClass': 'active',
      }], {
        sidebarAtIdx: 8,
        submenuAtIdx: 2,
      })
    },
  },

  async created () {
    this.fetchData()
  },

  beforeMount () {
    this.initPage({
      section: 'app',
      name: this.$t('app.inventory.title'),
      current: {
        app_name: 'inventory',
        app_id: 3,
        module_id: 9,
      },
    })
    sidebarModule.init(this.getObject(require('../sidebar.json')))
    sidebarModule.update(this.sidebar, 1, submenuModule.getWarehouses(this.warehouses))
    sidebarModule.update(this.sidebar, 8, [{
      'name': this.query.name,
      'router': {
        'name': 'WarehouseEdit',
        'query': {
          id: this.query.id,
          name: this.query.name,
        },
      },
      'activeClass': 'active',
    }], {
      sidebarAtIdx: 8,
      submenuAtIdx: 2,
    })
  },

  destroyed () {
    this.leavePage(this.$Progress, [])
  },

  methods: {
    async fetchData () {
      this.loading = true
      await Promise.all([
        this.$store.dispatch('getProducts'),
        this.$store.dispatch('getWarehouses'),
        this.$store.dispatch('getWarehouse', this.query.id),
        this.$store.dispatch('getUsedLotSerialSkus'),
      ])
      this.loading = false
      this.showFinish(this.$Progress)
    },
    getFormUpdate (form = {}) {
      return {
        'warehouse_id': this.query.id,
        'warehouse_name': form.name,
        'short_name': form.nameShort.toUpperCase(),
        'address': form.address,
        'primary': form.primary,
      }
    },
    getFormAssign (form = {}) {
      return {
        'skus': form.assignSkus.map((item) => {
          return {
            'sku_id': item.value,
            'is_use_lot': item.useLot,
            'is_use_serial_number': item.useSerialNumber,
          }
        }),
      }
    },
    getFormInsertBins (form = {}) {
      const bins = []
      for (const bin of form.binLocations) {
        if (!bin.id) {
          bins.push({
            'code': bin.code,
          })
        }
      }
      return {
        'bins': bins,
      }
    },
    getFormUpdateBins (form = {}) {
      const bins = []
      for (const bin of form.binLocations) {
        if (bin.id) {
          bins.push({
            'bin_location_id': bin.id,
            'code': bin.code,
          })
        }
      }
      return {
        'bins': bins,
      }
    },
    getFormDeleteBins (form = {}) {
      const bins = []
      for (const bin of this.warehouse.bin_locations) {
        const found = form.binLocations.some((item) => item.id === bin._id)
        if (!found) {
          bins.push({
            'bin_location_id': bin._id,
          })
        }
      }
      return {
        'bins': bins,
      }
    },
    async handleSubmit (form = {}) {
      this.disabled = true
      this.isSubmitted = true
      const formUpdate = this.getFormUpdate(form)
      const formAssign = this.getFormAssign(form)
      const formInsertBins = this.getFormInsertBins(form)
      const formUpdateBins = this.getFormUpdateBins(form)
      const formDeleteBins = this.getFormDeleteBins(form)
      const responseUpdate = await this.$store.dispatch('updateWarehouse', formUpdate)
      const responseAssignSkus = await this.$store.dispatch('updateWarehouseAssignSkus', { id: this.query.id, data: formAssign })
      if (formInsertBins.bins.length) {
        await this.$store.dispatch('insertWarehouseBins', { id: this.query.id, data: formInsertBins })
      }
      if (formUpdateBins.bins.length) {
        await this.$store.dispatch('updateWarehouseBins', { id: this.query.id, data: formUpdateBins })
      }
      if (formDeleteBins.bins.length) {
        await this.$store.dispatch('deleteWarehouseBins', { id: this.query.id, data: formDeleteBins })
      }
      if (responseUpdate.success && responseAssignSkus.success) {
        this.disabled = false
        this.isSubmitted = false
        this.notify(this.$vs, {
          text: this.$t('message.hasbeenupdated', { msg: this.warehouse.warehouse_name }),
          color: 'success',
          icon: 'cloud',
        })
        this.fetchData()
      }
    },
    handleSelectOption (value) {
      if (value === 'delete') {
        if (this.warehouse.primary) {
          $(`#${this.deletePrimaryWarehoseWarningModal}`).modal('show')
        } else {
          this.onOpenModal()
        }
      }
    },
    onOpenModal () {
      this.modal.show = true
    },
    handleCloseModal () {
      this.modal.show = false
    },
    handleCancleModal () {
      this.modal.show = false
    },
    async handleSumitModal () {
      this.modal.loading = true
      const response = await this.$store.dispatch('deleteWarehouse', { warehouse_id: this.query.id })
      if (response.success) {
        this.notify(this.$vs, {
          text: this.$t('message.hasbeendeleted', { msg: this.warehouse.warehouse_name }),
          color: 'danger',
          icon: 'delete',
        })
        this.modal.loading = false
        this.modal.show = false
        this.$router.push({ name: 'WarehouseList' })
      }
    },
  },
}
</script>



// WEBPACK FOOTER //
// src/components/apps/inventory/warehouse/Edit.vue